import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { isStandalone, useNative, usePhone } from "../../shared/phone/media";
import { installPrompt, onInstalled } from "../../shared/phone/shell";
import { Sheet } from "../../shared/phone/Sheet";
import { useAuth } from "../context/AuthContext";
import { IN_APP } from "../lib/inApp";
import { Icon } from "./ui/Icon";

interface MenuElement extends HTMLElement {
  setDeviceItems(items: { label: string; value?: string; onSelect: () => void }[]): void;
}

/**
 * The screen's short name, which the menu tells Flashsuite for Pulse (docs/INTEGRATION.md section 16). Nothing
 * else about the page leaves it.
 */
const PLACES: [RegExp, string][] = [
  [/^\/command/, "Command Center"],
  [/^\/(products\/[^/]+\/)?goals\/new/, "New goal"],
  [/^\/goals\/[^/]+\/ready/, "Get ready"],
  [/^\/goals\/[^/]+\/audience/, "Audience"],
  [/^\/goals\/[^/]+\/ads/, "Ads"],
  [/^\/goals\/[^/]+\/plan/, "Plan"],
  [/^\/goals\/[^/]+\/log/, "Log"],
  [/^\/goals\/[^/]+\/edit/, "Edit goal"],
  [/^\/goals\/[^/]+\/chat/, "Goal chat"],
  [/^\/goals\//, "Goal"],
  [/^\/products\/new/, "New product"],
  [/^\/products\/[^/]+\/profile/, "Business profile"],
  [/^\/products\/[^/]+\/partners/, "Partners"],
  [/^\/products\/[^/]+\/payrun/, "Pay run"],
  [/^\/products\/[^/]+\/agreement/, "Partner agreement"],
  [/^\/products\/[^/]+/, "Product"],
  [/^\/products/, "Products"],
  [/^\/partners\//, "Partner"],
  [/^\/library\/lessons/, "Lessons"],
  [/^\/library\/[^/]+/, "Library item"],
  [/^\/library/, "Library"],
  [/^\/connections/, "Connections"],
  [/^\/needs/, "Needs you"],
  [/^\/more/, "More"],
];
const placeOf = (path: string) => PLACES.find(([re]) => re.test(path))?.[1] ?? "Flashadz";

/** The business the session works in and the screen's place, which the menu (or, where it is not, the bar) carries. */
export function useFlashsuiteHere(): { business: string; place: string } {
  const { user } = useAuth();
  const { pathname } = useLocation();
  return { business: user?.businessId ?? "", place: placeOf(pathname) };
}

/**
 * Load Flashsuite's menu script once, from the Flashsuite this deployment signs in with (/auth/me), never a
 * fixed address: it defines `<flashsuite-menu>` and `<flashsuite-bar>`, which talk to that Flashsuite.
 */
export function useFlashsuiteScript(): void {
  const url = useAuth().flashsuite?.url;
  useEffect(() => {
    if (!url) return;
    const src = `${url}/menu/v1.js`;
    if (document.querySelector(`script[src="${src}"]`)) return;
    const script = document.createElement("script");
    script.src = src;
    script.defer = true;
    document.head.appendChild(script);
  }, [url]);
}

/**
 * Flashsuite's bar across the top of the app; it shows only when the business's payment failed. It carries the
 * business and place too: a phone keeps the menu on More, and on its other screens the menu's parts read the bar.
 */
export function FlashsuiteBar() {
  const { flashsuite } = useAuth();
  const here = useFlashsuiteHere();
  return flashsuite ? <flashsuite-bar app={flashsuite.app} business={here.business} place={here.place} /> : null;
}

const isIOS = () => /iPhone|iPad|iPod/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

/**
 * Flashsuite's menu (docs/INTEGRATION.md section 16), where the account menu was: the business, its other
 * apps, the person, their account, Appearance and Log out. `compact` is its one button, for a phone's title
 * row and the iPad's rail. Flashadz adds this device's one item, Add to
 * home screen, on a phone or an iPad; Safari has no prompt, so it shows the steps instead. Parts of a page that
 * change every frame or show a secret carry `fs-norecord`, so the menu's Show & tell leaves them out.
 */
export function FlashsuiteMenu({ compact = false }: { compact?: boolean }) {
  const { flashsuite } = useAuth();
  const here = useFlashsuiteHere();
  const native = useNative();
  const phone = usePhone();
  const ref = useRef<MenuElement>(null);
  const [steps, setSteps] = useState(false);

  useEffect(() => {
    let alive = true;
    const give = () => {
      // Why not inside the Flashsuite phone app: Flashadz is already in an app there.
      const canAdd = native && !IN_APP && !isStandalone() && (isIOS() || !!installPrompt());
      ref.current?.setDeviceItems(canAdd ? [{ label: "Add to home screen", onSelect: () => void addToHome() }] : []);
    };
    const addToHome = async () => {
      const offer = installPrompt();
      if (!offer) {
        setSteps(true);
        return;
      }
      await offer.prompt();
      if ((await offer.userChoice).outcome === "accepted") {
        onInstalled();
        give();
      }
    };
    // Why: the menu's script loads after the page (defer), so its method exists only once it is defined.
    void customElements.whenDefined("flashsuite-menu").then(() => alive && give());
    return () => {
      alive = false;
    };
  }, [native]);

  if (!flashsuite) return null;
  return (
    <>
      <flashsuite-menu ref={ref} app={flashsuite.app} business={here.business} place={here.place} compact={compact ? "" : undefined} />
      <Sheet
        open={steps}
        onClose={() => setSteps(false)}
        title="Add to home screen"
        right={
          <button type="button" className="pn-tbtn strong" onClick={() => setSteps(false)}>
            Done
          </button>
        }
      >
        <div className="pn-grp mo-steps-list">
          <div className="pn-row static wrap">
            <span className="mo-num">1</span>
            <span className="m">
              <span className="t">
                Tap Share <Icon name="share" />
              </span>
              <span className="s">{phone ? "In Safari's bar at the bottom." : "At the top of Safari."} If you do not see it, tap the three dots first.</span>
            </span>
          </div>
          <div className="pn-row static wrap">
            <span className="mo-num">2</span>
            <span className="m">
              <span className="t">
                Tap Add to Home Screen <Icon name="addHome" />
              </span>
              <span className="s">Scroll down the list if you do not see it.</span>
            </span>
          </div>
          <div className="pn-row static wrap">
            <span className="mo-num">3</span>
            <span className="m">
              <span className="t">Tap Add</span>
              <span className="s">Flashadz opens from its own icon, like an app.</span>
            </span>
          </div>
        </div>
      </Sheet>
    </>
  );
}
